import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Activity, LayoutDashboard, Users, LogOut, Stethoscope, CalendarDays, Wallet, FileSpreadsheet, FlaskConical, Settings as SettingsIcon, ShieldCheck } from "lucide-react";
import NoticesBanner from "@/components/NoticesBanner";
import Paywall from "@/components/Paywall";

const NAV = [
  { to: "/", label: "Painel", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/pacientes", label: "Pacientes", icon: Users, testid: "nav-patients" },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, testid: "nav-agenda" },
  { to: "/laboratorio", label: "Laboratório", icon: FlaskConical, testid: "nav-lab" },
  { to: "/financeiro", label: "Financeiro", icon: Wallet, testid: "nav-finance" },
  { to: "/convenios", label: "Convênios / TISS", icon: FileSpreadsheet, testid: "nav-tiss" },
  { to: "/configuracoes", label: "Configurações", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [plan, setPlan] = useState(null);

  useEffect(() => { api.get("/me/plan").then((r) => setPlan(r.data)).catch(() => {}); }, []);

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-64 shrink-0 bg-card border-r border-border flex flex-col fixed h-screen">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <Activity className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="font-heading font-extrabold text-lg leading-none tracking-tight">LIA <span className="text-ai">Clínica</span></h1>
              <p className="text-[11px] text-muted-foreground mt-0.5">Inteligência Aplicada à Saúde</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </Link>
            );
          })}
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                isActive("/admin") ? "bg-ai text-white" : "text-ai hover:bg-ai/10"}`}>
              <ShieldCheck className="w-4 h-4" /> Administração
            </Link>
          )}
        </nav>

        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="w-9 h-9">
              <AvatarFallback className="bg-ai/15 text-ai font-semibold text-sm">
                {(user?.name || "U").slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                <Stethoscope className="w-3 h-3" /> {user?.role || "médico"}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            data-testid="logout-btn"
            className="w-full justify-start text-muted-foreground hover:text-destructive mt-1"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
          >
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>
      </aside>

      <main className="flex-1 ml-64 min-h-screen">
        {plan?.blocked ? (
          <Paywall plan={plan} />
        ) : (
          <>
            {plan?.is_trial && plan?.days_left != null && (
              <div className="bg-ai/10 border-b border-ai/20 px-6 py-2.5 text-sm flex items-center gap-2" data-testid="trial-banner">
                <ShieldCheck className="w-4 h-4 text-ai" />
                <span>Período de avaliação: <strong>{plan.days_left} dia(s)</strong> restantes. Assine para não perder o acesso.</span>
                <Link to="/configuracoes" className="ml-auto text-ai font-medium hover:underline" data-testid="trial-upgrade-link">Ver planos</Link>
              </div>
            )}
            <NoticesBanner />
            <Outlet />
          </>
        )}
      </main>
    </div>
  );
}
